"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { canOptimizeImage } from "@/lib/images";

export type TestimonialItem = {
  id: string;
  name: string;
  company: string;
  role: string;
  rating: number;
  text: string;
  /** Optional: Avatar-URL aus DB. Wenn nicht gesetzt → Initialen-Fallback */
  avatarUrl?: string | null;
  /** Branche, Projekt-Link und Ergebnis – optional, erscheinen nur wenn gepflegt. */
  industry?: string | null;
  projectUrl?: string | null;
  result?: string | null;
};

/**
 * Endlos-Karussell für Kundenstimmen.
 *
 * - Maximal 3 Karten nebeneinander (lg), 2 auf Tablets, 1 auf Handys.
 * - Pfeile links/rechts, Tastatur (←/→) und Wischen auf Touch-Geräten.
 * - „Unendlich": vor und hinter der echten Liste liegen Klone der jeweils
 *   ersten/letzten Karten. Landet die Animation auf einem Klon, springen wir
 *   ohne Übergang auf das echte Gegenstück – für das Auge eine nahtlose
 *   Schleife.
 * - Passen alle Stimmen gleichzeitig ins Raster, gibt es keine Pfeile,
 *   sondern ein ganz normales Grid.
 */

const TRANSITION_MS = 500;

function initials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function usePerView(): number {
  // SSR-Default: 3 (Desktop). Auf dem Client sofort korrigiert.
  const [perView, setPerView] = useState(3);
  useEffect(() => {
    const mdQuery = window.matchMedia("(min-width: 768px)");
    const lgQuery = window.matchMedia("(min-width: 1024px)");
    const update = () => setPerView(lgQuery.matches ? 3 : mdQuery.matches ? 2 : 1);
    update();
    mdQuery.addEventListener("change", update);
    lgQuery.addEventListener("change", update);
    return () => {
      mdQuery.removeEventListener("change", update);
      lgQuery.removeEventListener("change", update);
    };
  }, []);
  return perView;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const q = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(q.matches);
    update();
    q.addEventListener("change", update);
    return () => q.removeEventListener("change", update);
  }, []);
  return reduced;
}

function TestimonialCard({ item }: { item: TestimonialItem }) {
  return (
    <article
      className="flex h-full flex-col rounded-2xl border p-6 lg:p-8"
      style={{
        backgroundColor: "var(--card-bg)",
        borderColor: "var(--border)",
        boxShadow: "var(--card-shadow)",
      }}
    >
      <div className="flex gap-1" role="img" aria-label={`${item.rating} von 5 Sternen`}>
        {Array.from({ length: item.rating }).map((_, j) => (
          <Star key={j} className="h-5 w-5 fill-amber-400 text-amber-400" aria-hidden="true" />
        ))}
      </div>
      <p className="mt-6 flex-1 text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>
        &ldquo;{item.text}&rdquo;
      </p>
      <div className="mt-6 flex items-center gap-3">
        {item.avatarUrl ? (
          canOptimizeImage(item.avatarUrl) ? (
            <Image
              src={item.avatarUrl}
              alt={item.name}
              width={40}
              height={40}
              className="h-10 w-10 rounded-full object-cover"
              style={{ backgroundColor: "var(--bg-secondary)", border: "1px solid var(--border)" }}
            />
          ) : (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={item.avatarUrl}
              alt={item.name}
              width={40}
              height={40}
              loading="lazy"
              className="h-10 w-10 rounded-full object-cover"
              style={{ backgroundColor: "var(--bg-secondary)", border: "1px solid var(--border)" }}
            />
          )
        ) : (
          <div
            className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold text-white"
            // brand-700 statt brand-500 wegen WCAG-AA-Kontrast der weißen Initialen.
            style={{ backgroundColor: "var(--color-brand-700)" }}
            aria-hidden="true"
          >
            {initials(item.name)}
          </div>
        )}
        <div>
          <p className="text-sm font-semibold" style={{ color: "var(--text)" }}>
            {item.name}
          </p>
          <p className="text-sm" style={{ color: "var(--text-tertiary)" }}>
            {item.role ? `${item.role}, ` : ""}
            {item.company}
            {item.industry ? ` · ${item.industry}` : ""}
          </p>
          {item.projectUrl && (
            <a
              href={item.projectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-block text-xs font-semibold text-brand-500 hover:underline"
            >
              Projekt ansehen (öffnet in neuem Tab)
            </a>
          )}
        </div>
      </div>
      {item.result && (
        <p className="mt-4 rounded-lg px-3 py-2 text-sm font-medium" style={{ backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }}>
          Ergebnis: {item.result}
        </p>
      )}
    </article>
  );
}

function ArrowButton({
  direction,
  onClick,
  className = "",
}: {
  direction: "prev" | "next";
  onClick: () => void;
  className?: string;
}) {
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === "prev" ? "Vorherige Kundenstimme" : "Nächste Kundenstimme"}
      className={`flex h-11 w-11 items-center justify-center rounded-full border transition-all duration-150 hover:border-brand-500 hover:text-brand-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 active:scale-95 ${className}`}
      style={{
        backgroundColor: "var(--card-bg)",
        borderColor: "var(--border)",
        color: "var(--text-secondary)",
        boxShadow: "var(--card-shadow-md)",
      }}
    >
      <Icon className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}

export function TestimonialsCarousel({ items }: { items: TestimonialItem[] }) {
  const perView = usePerView();
  const reducedMotion = usePrefersReducedMotion();
  const count = items.length;
  const loop = count > perView;

  // Index innerhalb der erweiterten Liste (Klone vorn + echte + Klone hinten).
  // Zusätzlich als Ref, damit Timer/Handler immer den aktuellen Wert sehen.
  const [index, setIndexState] = useState(perView);
  const indexRef = useRef(perView);
  const setIndex = useCallback((i: number) => {
    indexRef.current = i;
    setIndexState(i);
  }, []);
  const [animate, setAnimate] = useState(true);
  const animating = useRef(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartX = useRef<number | null>(null);

  // Erweiterte Liste: [letzte perView] + alle + [erste perView]
  const extended = useMemo(() => {
    if (!loop) return items;
    const head = items.slice(-perView);
    const tail = items.slice(0, perView);
    return [...head, ...items, ...tail];
  }, [items, perView, loop]);

  // Wenn sich die Spaltenzahl ändert, sauber auf die erste echte Karte springen.
  useEffect(() => {
    setAnimate(false);
    setIndex(perView);
    animating.current = false;
  }, [perView, setIndex]);

  useEffect(
    () => () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
    },
    [],
  );

  /**
   * Nach einer Bewegung: Sperre lösen und – falls wir auf einem Klon stehen –
   * ohne Übergang auf das echte Gegenstück springen. Wird von transitionend
   * ausgelöst, zur Sicherheit aber auch per Timer (transitionend bleibt z. B.
   * in einem unsichtbaren Tab aus – sonst würde das Karussell hängen).
   */
  const settle = useCallback(() => {
    if (settleTimer.current) {
      clearTimeout(settleTimer.current);
      settleTimer.current = null;
    }
    animating.current = false;
    const i = indexRef.current;
    if (i >= count + perView) {
      setAnimate(false);
      setIndex(i - count);
    } else if (i < perView) {
      setAnimate(false);
      setIndex(i + count);
    }
  }, [count, perView, setIndex]);

  const go = useCallback(
    (delta: number) => {
      if (!loop || animating.current) return;
      animating.current = true;
      setAnimate(!reducedMotion);
      setIndex(indexRef.current + delta);
      settleTimer.current = setTimeout(settle, reducedMotion ? 30 : TRANSITION_MS + 120);
    },
    [loop, reducedMotion, setIndex, settle],
  );

  const onTransitionEnd = (e: React.TransitionEvent) => {
    // Nur die Bewegung der Schiene zählt, keine Übergänge von Kind-Elementen.
    if (e.target !== e.currentTarget || e.propertyName !== "transform") return;
    settle();
  };

  // Nach einem Sprung ohne Übergang die Animation wieder einschalten
  // (im nächsten Frame, damit der Browser den Sprung nicht animiert).
  useEffect(() => {
    if (animate) return;
    const raf = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(raf);
  }, [animate, index]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(-1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      go(1);
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  // Position der aktuell ersten sichtbaren echten Karte (0-basiert).
  const current = loop ? (((index - perView) % count) + count) % count : 0;

  if (!loop) {
    return (
      <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {items.map((t) => (
          <TestimonialCard key={t.id} item={t} />
        ))}
      </div>
    );
  }

  return (
    <div
      className="relative mt-16"
      role="region"
      aria-roledescription="Karussell"
      aria-label="Kundenstimmen"
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      {/* Seitliche Pfeile (ab lg außen neben den Karten) */}
      <ArrowButton
        direction="prev"
        onClick={() => go(-1)}
        className="absolute top-1/2 -left-6 z-10 hidden -translate-y-1/2 lg:flex"
      />
      <ArrowButton
        direction="next"
        onClick={() => go(1)}
        className="absolute top-1/2 -right-6 z-10 hidden -translate-y-1/2 lg:flex"
      />

      <div
        className="-mx-3 overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        aria-live="polite"
      >
        <div
          className="flex"
          style={{
            transform: `translateX(-${(index * 100) / perView}%)`,
            transition: animate ? `transform ${TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)` : "none",
          }}
          onTransitionEnd={onTransitionEnd}
        >
          {extended.map((t, i) => {
            const isClone = i < perView || i >= count + perView;
            const realPos = ((((i - perView) % count) + count) % count) + 1;
            return (
              <div
                key={`${t.id}-${i}`}
                className="shrink-0 px-3"
                style={{ width: `${100 / perView}%` }}
                aria-hidden={isClone ? "true" : undefined}
                aria-label={isClone ? undefined : `Kundenstimme ${realPos} von ${count}`}
              >
                <TestimonialCard item={t} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Steuerung unten: Pfeile (bis lg) + Punkte */}
      <div className="mt-8 flex items-center justify-center gap-4">
        <ArrowButton direction="prev" onClick={() => go(-1)} className="lg:hidden" />
        <div className="flex items-center gap-2" aria-hidden="true">
          {items.map((t, i) => (
            <span
              key={t.id}
              className="block h-2 rounded-full transition-all duration-300"
              style={{
                width: i === current ? 24 : 8,
                backgroundColor: i === current ? "var(--color-brand-500)" : "var(--border)",
              }}
            />
          ))}
        </div>
        <ArrowButton direction="next" onClick={() => go(1)} className="lg:hidden" />
      </div>
    </div>
  );
}
